import type { ReactNode } from 'react'
import { useRef, useState, useEffect } from 'react'
import classNames from 'classnames'
import { ChevronUp } from 'src/components/Icons/ChevronUp'
import { ChevronDown } from 'src/components/Icons/ChevronDown'
import { slugify } from 'src/utils/slugfy'

import type { FacetValue } from '../types'
import './style.scss'

type Props = {
  title: string
  index: string | number
  onAccordionChange?: () => void
  children: ReactNode
  showSelectedValues?: boolean
  onCloseTab?: () => void
  selectedValues?: FacetValue[]
  subTitle?: string | null
  Icon?: JSX.Element
}

export function Accordion({
  title,
  index,
  onAccordionChange,
  onCloseTab,
  children,
  selectedValues,
  subTitle = null,
  showSelectedValues = true,
  Icon,
}: Readonly<Props>) {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const [height, setHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  const toggleAccordion = () => {
    setIsAccordionOpen(!isAccordionOpen)
    onAccordionChange?.()
    if (isAccordionOpen) {
      onCloseTab?.()
    }
  }

  useEffect(() => {
    const content = contentRef.current

    if (!content) {
      return
    }

    if (!isAccordionOpen) {
      setHeight(0)

      return
    }

    const measure = () => {
      const inner = content.firstElementChild

      if (!(inner instanceof HTMLElement)) {
        setHeight(content.scrollHeight)

        return
      }

      // scrollHeight ignores the child's own margin. FacetSection uses mt-4,
      // and overflow:hidden on this panel clips that gap off the bottom border.
      const styles = window.getComputedStyle(inner)
      const marginTop = Number.parseFloat(styles.marginTop) || 0
      const marginBottom = Number.parseFloat(styles.marginBottom) || 0
      const nextHeight = Math.ceil(
        inner.scrollHeight + marginTop + marginBottom
      )

      setHeight((current) => (current === nextHeight ? current : nextHeight))
    }

    measure()

    const inner = content.firstElementChild

    if (typeof ResizeObserver === 'undefined' || !inner) {
      return
    }

    const observer = new ResizeObserver(() => {
      measure()
    })

    observer.observe(inner)

    return () => observer.disconnect()
  }, [isAccordionOpen, children])

  const facetNameId = slugify(title)
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '')

  const isReviewsFacet = facetNameId === 'avaliacoes'

  return (
    <div
      className={classNames(
        'accordion-item border-b border-gray py-6 last:border-b-none',
        {
          hidden: isReviewsFacet,
        }
      )}
      data-accordion-item
      data-testid="filter-accordion-item"
      data-fs-facets-accordion-item
      data-dy-id={`accordion-item-${facetNameId || index}`}
    >
      <button
        className="header-tab-accordion-filter flex justify-between items-center w-full"
        onClick={toggleAccordion}
        tabIndex={Number(index)}
        aria-expanded={isAccordionOpen}
        aria-controls={`accordion-content-${index}`}
        data-accordion-button
        data-testid="filter-accordion-button"
      >
        <span className="flex items-center">
          <div className="flex flex-col gap-2">
            <span className="flex items-center gap-2 text-base text-restructure-primary font-medium font-inter text-left leading-[21px]">
              {!!Icon && Icon}
              {title}
            </span>
            {subTitle
              ? !isAccordionOpen && (
                  <small className="text-xs text-tertiary font-normal font-inter text-left">
                    {subTitle}
                  </small>
                )
              : selectedValues &&
                !isAccordionOpen &&
                showSelectedValues && (
                  <small className="text-xs text-tertiary font-normal font-inter text-left empty:hidden">
                    {selectedValues.map((value) => value.label).join(', ')}
                  </small>
                )}
          </div>
        </span>
        {isAccordionOpen ? <ChevronUp /> : <ChevronDown />}
      </button>

      <div
        id={`accordion-content-${index}`}
        className="accordion-content"
        ref={contentRef}
        role="region"
        style={{
          maxHeight: `${height}px`,
          transition: 'max-height 0.3s ease',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  )
}
